import React, { useState, useEffect } from 'react';
import Rater from 'react-rater';
import { get, isEmpty } from 'lodash';
import { ProfileWrapper } from 'components/mentoring/styles';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { mentoringReviewCreate, getMentoringReview } from 'libs/api/mentoring';
import { getMentor } from 'libs/api/user';
import { useDataApi } from 'libs/hooks';
import { showNotification } from 'libs/utils';
import { TMentoringReview } from 'modules/mentoring';
import { TModalProps } from 'modules/types';
import { TMentor } from 'modules/user';
import { ENotificationType } from 'enums/base.enum';
import { Avatar, Input, Modal, Button, Col, Row, Typography, Form } from 'antd';
import { FormWrapper } from 'GlobalStyles';

const { Text, Title } = Typography;
const MentoringReviewModal = ({ modalState, setModalState }: TModalProps) => {
  const getPromise = getMentor.bind(null, get(modalState, 'record.mentor.id'));
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const [comment, setComment] = useState(<div style={{ color: '#aeaeae' }}>별점을 선택해주세요</div>);
  const [ratingValue, setRatingValue] = useState(0);
  const [form] = Form.useForm();
  const [{ data: mentor, loading }, setCallback, setExecuting] = useDataApi<TMentor>(getPromise, {}, false);
  const getReviewPromise = getMentoringReview.bind(null, get(modalState, 'record.review'));
  const [{ data: review, loading: reviewLoading }, setReviewCallback, setReviewExecuting] = useDataApi<
    TMentoringReview
  >(getReviewPromise, {}, false);

  useEffect(() => {
    if (modalState.visible) {
      setExecuting(true);
      setCallback(() => getPromise);
      setComment(<div style={{ color: '#aeaeae' }}>별점을 선택해주세요</div>);
      setRatingValue(0);
      if (get(modalState, 'isView', false) === true) {
        setReviewExecuting(true);
        setReviewCallback(() => getReviewPromise);
      }
    }
  }, [modalState.visible]);

  useEffect(() => {
    if (isEmpty(review) === false) {
      form.setFields([{ name: 'comment', value: get(review, 'comment') }]);
      handleRate({ rating: get(review, 'score') });
    }
  }, [review]);

  function handleSubmit() {
    async function handleCreateReview(value: TMentoringReview) {
      try {
        value.mentoring_class_id = get(modalState, 'record.id');
        value.score = ratingValue;
        const response = await mentoringReviewCreate(value);
        setSubmitButtonDisabled(true);
        modalState.onFinish && modalState.onFinish(get(response, 'data.id'));
        handleCancel();
        showNotification(ENotificationType.Success, '멘토 평가를 완료했습니다.');
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
    form
      .validateFields()
      .then((value) => {
        handleCreateReview(value as TMentoringReview);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  function handleCancel() {
    setModalState({ visible: false });
    form.resetFields();
  }

  function handleRate({ rating }: { rating: number }) {
    setRatingValue(rating);
    switch (rating) {
      case 1:
        setComment(<div style={{ color: '#ffce50' }}>1점. 별로에요</div>);
        break;
      case 2:
        setComment(<div style={{ color: '#ffce50' }}>2점. 그저그래요</div>);
        break;
      case 3:
        setComment(<div style={{ color: '#ffce50' }}>3점. 괜찮아요</div>);
        break;
      case 4:
        setComment(<div style={{ color: '#ffce50' }}>4점. 좋아요</div>);
        break;
      case 5:
        setComment(<div style={{ color: '#ffce50' }}>5점. 최고에요</div>);
        break;
    }
  }

  return (
    <Modal
      visible={modalState.visible}
      maskClosable={true}
      closable={true}
      footer={
        <Row justify='end'>
          <Button onClick={handleCancel}>닫기</Button>
          {get(modalState, 'isView', false) === false && (
            <Button loading={submitButtonDisabled} type='primary' onClick={handleSubmit}>
              등록
            </Button>
          )}
        </Row>
      }
      width={400}
      centered={true}
      title={
        <Title level={4} style={{ marginBottom: '0px' }}>
          {get(modalState, 'isView', false) === false ? '평가하기' : '평가보기'}
        </Title>
      }
      onCancel={handleCancel}
    >
      <div className='border-box'>
        <Row align='middle'>
          <Col flex='100px'>
            <Avatar
              style={{ borderRadius: '50%', width: '100%', height: 'auto' }}
              src={get(mentor, 'user.profile_image.absolute_url')}
            />
          </Col>
          <Col flex='auto'>
            <ProfileWrapper style={{ marginLeft: '20px' }}>
              <div className='name'>{get(mentor, 'user.fullname')}</div>
              <div className='intro'>"{get(mentor, 'short_introduction')}"</div>
              <div style={{ lineHeight: '30px' }}>
                <Rater interactive={false} rating={get(mentor, 'score_average')} />
                <span style={{ marginLeft: '5px' }} className='score'>
                  {get(mentor, 'score_average')}
                </span>
                <span className='num-of-rater'>&nbsp;({get(mentor, 'num_of_rater', 0)})</span>
              </div>
            </ProfileWrapper>
          </Col>
        </Row>
      </div>
      <div className='rater-interactive'>
        <Rater interactive={!get(modalState, 'isView', false)} rating={ratingValue} onRate={handleRate} />
        {comment}
      </div>
      <FormWrapper style={{ padding: '20px 0px' }}>
        <Form form={form} layout='vertical' className='form-normal'>
          <Form.Item name='comment' className='form-item no-margin force-black' label='어떤 점이 좋았나요?'>
            <Input.TextArea
              className='form_textarea'
              placeholder='최소 10자 이상 입력해주세요'
              autoSize={{ minRows: 5 }}
              disabled={get(modalState, 'isView', false)}
            />
          </Form.Item>
        </Form>
      </FormWrapper>
    </Modal>
  );
};

export default MentoringReviewModal;
